import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import TripDetails from './components/TripDetails';
import ClientDetails from './components/ClientDetails';
import ClientItinerary from './components/ClientItinerary';
import Login from './components/Login';
import Register from './components/Register';
import ProposalDetails from './components/ProposalDetails';
import ClientProposals from './components/ClientProposals';
import ManageProposalHotels from './components/ManageProposalHotels';
import ProposalViewer from './components/ProposalViewer';
import ClientIndex from './components/ClientIndex';
import AdminIndex from './components/AdminIndex';
import LandingPage from './components/LandingPage';

function TripDetailsWrapper() {
  const { id } = useParams();
  return <TripDetails tripId={id} />;
}

function ClientDetailsWrapper() {
  const { id } = useParams();
  return <ClientDetails clientId={id} />;
}

function ProposalDetailsWrapper() {
  const { id } = useParams();
  return <ProposalDetails />;
}

function ManageProposalHotelsWrapper() {
  const { id } = useParams();
  return <ManageProposalHotels />;
}

function ProposalViewerWrapper() {
  const { id } = useParams();
  return <ProposalViewer />;
}

function HomeRoute() {
  const { user, loading } = React.useContext(AuthContext);
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  return user.role === 'admin' ? <Navigate to="/admin-dashboard" /> : <Navigate to="/client-dashboard" />;
}

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = React.useContext(AuthContext);
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on user role
    return user.role === 'admin' ? <Navigate to="/admin-dashboard" /> : <Navigate to="/client-dashboard" />;
  }
  return children;
};

function ErrorBoundary({ children }) {
  const [error, setError] = useState(null);
  useEffect(() => {
    if (error) console.error('Caught error:', error);
  }, [error]);
  try {
    return children;
  } catch (err) {
    setError(err);
    return <div>Error: {err.message}</div>;
  }
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/app" element={<HomeRoute />} />
            <Route 
              path="/trips/:id" 
              element={<PrivateRoute allowedRoles={['admin']}><TripDetailsWrapper /></PrivateRoute>} 
            />
            <Route 
              path="/clients/:id" 
              element={<PrivateRoute allowedRoles={['admin']}><ClientDetailsWrapper /></PrivateRoute>} 
            />
            <Route 
              path="/proposals/:id" 
              element={<PrivateRoute allowedRoles={['admin']}><ProposalDetailsWrapper /></PrivateRoute>} 
            />
            <Route 
              path="/proposals/:id/manage" 
              element={<PrivateRoute allowedRoles={['admin']}><ManageProposalHotelsWrapper /></PrivateRoute>} 
            />
            <Route 
              path="/proposals/:id/preview" 
              element={<PrivateRoute allowedRoles={['admin']}><ProposalViewerWrapper /></PrivateRoute>} 
            />
            <Route 
              path="/my-proposals" 
              element={<PrivateRoute allowedRoles={['client']}><ClientProposals /></PrivateRoute>} 
            />
            <Route 
              path="/client-dashboard" 
              element={<PrivateRoute allowedRoles={['client']}><ClientIndex /></PrivateRoute>} 
            />
            <Route 
              path="/admin-dashboard" 
              element={<PrivateRoute allowedRoles={['admin']}><AdminIndex /></PrivateRoute>} 
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;